import {acceptHMRUpdate, defineStore} from 'pinia';
import {computed, ref, unref} from 'vue';
import type {MaybeRef, Ref} from 'vue';
import type {
	NewGivenAnswer,
	RieProgressResource,
	ThemeResource,
	TopicDetailResource
} from '~/lib/types/inventarisationTypes';
import useApi from '~/composables/useApi';
import type {ProblemDetail} from '~/lib/types/fetchErrors';
import type {TopicRelevanceUpdateRequest, TopicStatus} from '~/lib/types/topicTypes';
import {getFilenameFromContentDisposition, saveBlob} from '~/lib/download';

export const useInventarisationStore = defineStore('inventarisation', () => {
	const {GET, POST, PATCH} = useApi();

	const activeRie = ref<string>();

	const progress = ref<RieProgressResource | null>(null);
	const progressLoading = ref(false);

	const themes = ref<ThemeResource[]>([]);
	const topics = ref<TopicDetailResource[]>([]);
	const loading = ref(false);
	const waitingForExport = ref(false);
	const problemDetail = ref<ProblemDetail>();

	const getTopic = computed(() => (topicKey: string | Ref<string>): undefined | TopicDetailResource => {
		const topic = topics.value.find(({topic}) => topic.key === unref(topicKey));
		if (topic === undefined) {
			return undefined;
		}
		return {
			...topic
		} as TopicDetailResource;
	});

	const getQuestion = computed(() => (topicKey: string | Ref<string>, questionKey: string | Ref<string>) => {
		const topic = topics.value.find(({topic}) => topic.key === unref(topicKey));
		if (topic) {
			const activeQuestion = topic.questions.find((question) => question.key === unref(questionKey));
			if (activeQuestion) {
				return activeQuestion;
			}
			return topic.archivedQuestions.find((question) => question.key === unref(questionKey));
		}
		return undefined;
	});

	const getNextQuestion = computed(() => (topicKey: string | Ref<string>, questionKey: string | Ref<string>) => {
		const topic = topics.value.find(({topic}) => topic.key === unref(topicKey));
		if (topic) {
			const questionIndex = topic.questions.findIndex((question) => question.key === unref(questionKey));
			if (questionIndex !== -1) {
				let nextStep = 1;

				while (topic.questions[questionIndex + nextStep]?.skip ?? false) {
					nextStep = nextStep + 1;
				}

				return topic.questions[questionIndex + nextStep];
			}
		}
		return undefined;
	});

	const getPreviousQuestion = computed(() => (topicKey: string | Ref<string>, questionKey: string | Ref<string>) => {
		const topic = topics.value.find(({topic}) => topic.key === unref(topicKey));
		if (topic) {
			const questionIndex = topic.questions.findIndex((question) => question.key === unref(questionKey));
			if (questionIndex !== -1) {
				let prevStep = 1;
				while (topic.questions[questionIndex - prevStep]?.skip ?? false) {
					prevStep = prevStep + 1;
				}
				return topic.questions[questionIndex - prevStep];
			}
		}
		return undefined;
	});

	const getNextTopic = computed(() => (currentTopicKey: MaybeRef<string>) => {
		const currentKey = unref(currentTopicKey);

		const flatArrayOfTopics = themes.value.flatMap((theme) => {
			return theme.topics.map((topic) => ({
				themeKey: theme.key,
				topicKey: topic.key,
				topicStatus: topic.status,
				topic
			}));
		});

		const currentIndex = flatArrayOfTopics.findIndex(({topicKey}) => topicKey === currentKey);

		// Return undefined if the current topic is not found or if the current topic is the last topic in the array
		if (currentIndex === -1 || currentIndex === flatArrayOfTopics.length - 1) {
			return undefined;
		}

		return flatArrayOfTopics.slice(currentIndex + 1)
			.find(({topicStatus}) => topicStatus === 'RELEVANT' || topicStatus === 'UNDECIDED')?.topic;
	});

	const getPreviousTopic = computed(() => (currentTopicKey: MaybeRef<string>) => {
		const currentKey = unref(currentTopicKey);

		const flatArrayOfTopics = themes.value.flatMap((theme) => {
			return theme.topics.map((topic) => ({
				themeKey: theme.key,
				topicKey: topic.key,
				topicStatus: topic.status,
				topic
			}));
		});

		const currentIndex = flatArrayOfTopics.findIndex(({topicKey}) => topicKey === currentKey);

		// Return undefined if the current topic is not found or if the current topic is the first topic in the array
		if (currentIndex === -1 || currentIndex === 0) {
			return undefined;
		}

		return flatArrayOfTopics.slice(0, currentIndex)
			.reverse()
			.find(({topicStatus}) => topicStatus === 'RELEVANT' || topicStatus === 'UNDECIDED')?.topic;
	});

	const clearData = () => {
		themes.value = [];
		topics.value = [];
	};

	const submitQuestion = async (rieId: string, newGivenAnswer: NewGivenAnswer) => {
		loading.value = true;
		const thisTopic = topics.value.find(({topic}) => topic.key === newGivenAnswer.topicKey)?.topic;
		loading.value = false;

		const {data, error} = await POST('/rie/{rieId}/question', {
			params: {
				path: {
					rieId: rieId.toString()
				}
			},
			body: newGivenAnswer
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;

			if (error.detail) {
				throw new Error(error.detail, {cause: error});
			}

			throw new Error('Opslaan antwoord niet gelukt', {
				cause: error
			});
		}

		const topicIndex = topics.value.findIndex(({topic}) => topic.key === thisTopic?.key);

		if (topicIndex !== -1) {
			topics.value[topicIndex] = data;
		} else {
			topics.value.push(data);
		}

		if (data.progress) {
			progress.value = data.progress;
		}

		loading.value = false;
		problemDetail.value = undefined;

		return true;
	};

	const fetchProgress = async (rieId: string) => {
		progressLoading.value = true;

		if (rieId !== activeRie.value) {
			clearData();
		}
		activeRie.value = rieId;

		const {data, error} = await GET('/rie/{rieId}/progress', {
			params: {
				path: {
					rieId
				}
			}
		});

		if (error || !data) {
			progressLoading.value = false;
			problemDetail.value = error;
			return false;
		}

		progress.value = data;
		progressLoading.value = false;
		return true;
	};

	const fetchThemes = async (rieId: string) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (rieId !== activeRie.value) {
			clearData();
		}
		activeRie.value = rieId;

		const {data, error} = await GET('/rie/{rieId}/themes', {
			params: {
				path: {
					rieId
				}
			}
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		themes.value = data;
		loading.value = false;
		return true;
	};

	const fetchTopic = async (rieId: string, topicKey: string) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (rieId !== activeRie.value) {
			clearData();
		}
		activeRie.value = rieId;

		const {data, error} = await GET('/rie/{rieId}/topics/{topicKey}', {
			params: {
				path: {
					rieId,
					topicKey
				}
			}
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		const topicIndex = topics.value.findIndex(({topic}) => topic.key === topicKey);

		if (topicIndex !== -1) {
			topics.value[topicIndex] = data;
		} else {
			topics.value.push(data);
		}

		loading.value = false;
		return true;
	};

	const updateTopicsRelevancy = async (rieId: string, topicsRelevancy: TopicRelevanceUpdateRequest[]) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {error} = await PATCH('/rie/{rieId}/topics/', {
			params: {
				path: {
					rieId
				}
			},
			body: topicsRelevancy
		});

		if (error) {
			loading.value = false;
			problemDetail.value = error;
			throw new Error('Opslaan thema\'s niet gelukt', {cause: error});
		}
	};

	const updateTopicRelevancy = async (rieId: string, topicKey: string | undefined, topicStatus: TopicStatus) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (!topicKey) {
			loading.value = false;
			problemDetail.value = {
				title: 'Thema niet gevonden',
				detail: 'Het thema dat je probeert te updaten bestaat niet.'
			};
			return false;
		}

		const {error} = await PATCH('/rie/{rieId}/topics/{topicKey}', {
			params: {
				path: {
					rieId,
					topicKey
				}
			},
			body: topicStatus
		});

		if (error) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		const topicIndex = topics.value.findIndex(({topic}) => topic.key === topicKey);
		themes.value = themes.value.map(theme => {
			theme.topics = theme.topics.map(topic => {
				if (topic.key === topicKey) {
					topic.status = topicStatus;
				}
				return topic;
			});
			return theme;
		});

		if (topicIndex !== -1) {
			topics.value[topicIndex].topic.status = topicStatus;
		}

		loading.value = false;
		fetchProgress(rieId);

		return true;
	};


	const downloadPdfExport = async (rieId: string) => {
		if (waitingForExport.value) {
			return false;
		}
		waitingForExport.value = true;
		problemDetail.value = undefined;

		const {data, error, response} = await GET('/rie/{rieId}/questions/export', {
			params: {
				path: {rieId}
			},
			parseAs: 'blob'
		});

		if (error || !data) {
			problemDetail.value = error;
			waitingForExport.value = false;
			return false;
		}

		const filename = getFilenameFromContentDisposition(response, 'vragenlijst.pdf');
		saveBlob(data, filename);
		waitingForExport.value = false;
	};

	return {
		problemDetail,
		progress,
		themes,
		topics,
		loading,
		progressLoading,
		getTopic,
		getQuestion,
		getNextQuestion,
		getPreviousQuestion,
		getNextTopic,
		getPreviousTopic,
		waitingForExport,
		fetchTopic,
		fetchThemes,
		fetchProgress,
		submitQuestion,
		updateTopicRelevancy,
		updateTopicsRelevancy,
		downloadPdfExport,
		clearData
	};
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useInventarisationStore, import.meta.hot));
}
